// Money [MB D8; 03 BR-DM-005; ruling O-2]: document amounts = integer minor units + currency; unit
// prices/costs = integer micros (6 dp) + currency + per-UoM; extended = round-half-even(qty × rate).
import { z } from 'zod';
import { AppError } from '../kernel/errors.ts';
import { currencyCodeSchema, minorUnitsOf } from './currency.ts';
import type { CurrencyCode } from './currency.ts';
import {
  formatDecimal,
  parseDecimal,
  parseDecimalRounded,
  pow10,
  roundHalfEven,
  toSafeNumber,
} from './decimal.ts';
import { QTY_SCALE, uomSchema } from './qty.ts';
import type { Qty, Uom } from './qty.ts';

export interface Money {
  readonly amountMinor: number;
  readonly currency: CurrencyCode;
}

/** Unit price/cost: `micros` of `currency` per one `per` UoM. */
export interface UnitRate {
  readonly micros: number;
  readonly currency: CurrencyCode;
  readonly per: Uom;
}

export const RATE_SCALE = 6;
const MICROS = pow10(RATE_SCALE);
const QTY_ONE = pow10(QTY_SCALE);

/** Percentage held as integer ten-thousandths of a percent: numeric(7,4); 6 % = 60 000. */
export type Pct = number;
export const PCT_SCALE = 4;
const PCT_HUNDRED = 100n * pow10(PCT_SCALE);

function safe(n: number, what: string): number {
  if (!Number.isSafeInteger(n)) throw new RangeError(`${what} must be a safe integer`);
  return n === 0 ? 0 : n;
}

function currencyMismatch(a: CurrencyCode, b: CurrencyCode): AppError {
  return new AppError('CURRENCY_MISMATCH', {
    messageKey: 'errors.units.currencyMismatch',
    params: { left: a, right: b },
  });
}

export function money(amountMinor: number, currency: CurrencyCode): Money {
  minorUnitsOf(currency);
  return Object.freeze({ amountMinor: safe(amountMinor, 'amountMinor'), currency });
}

export function zeroMoney(currency: CurrencyCode): Money {
  return money(0, currency);
}

/** "12.34" CAD → 1234 (strict: more places than the currency allows is refused). */
export function moneyFromDecimal(text: string, currency: CurrencyCode): Money {
  return money(toSafeNumber(parseDecimal(text, minorUnitsOf(currency))), currency);
}

export function moneyToDecimal(m: Money): string {
  return formatDecimal(BigInt(m.amountMinor), minorUnitsOf(m.currency));
}

export const moneySchema = z.object({
  amountMinor: z.number().int().refine(Number.isSafeInteger),
  currency: currencyCodeSchema,
});

export function addMoney(a: Money, b: Money): Money {
  if (a.currency !== b.currency) throw currencyMismatch(a.currency, b.currency);
  return money(a.amountMinor + b.amountMinor, a.currency);
}

export function subtractMoney(a: Money, b: Money): Money {
  if (a.currency !== b.currency) throw currencyMismatch(a.currency, b.currency);
  return money(a.amountMinor - b.amountMinor, a.currency);
}

export function negateMoney(a: Money): Money {
  return money(-a.amountMinor, a.currency);
}

export function sumMoney(items: readonly Money[], currency: CurrencyCode): Money {
  return items.reduce((acc, m) => addMoney(acc, m), zeroMoney(currency));
}

export function multiplyMoney(a: Money, factor: number): Money {
  if (!Number.isSafeInteger(factor)) throw new RangeError('factor must be an integer');
  return money(toSafeNumber(BigInt(a.amountMinor) * BigInt(factor)), a.currency);
}

export function compareMoney(a: Money, b: Money): -1 | 0 | 1 {
  if (a.currency !== b.currency) throw currencyMismatch(a.currency, b.currency);
  return a.amountMinor < b.amountMinor ? -1 : a.amountMinor > b.amountMinor ? 1 : 0;
}

export function isZeroMoney(a: Money): boolean {
  return a.amountMinor === 0;
}

/** pct of an amount, half-even to the minor unit. */
export function percentOfMoney(a: Money, pct: Pct): Money {
  return money(
    toSafeNumber(roundHalfEven(BigInt(a.amountMinor) * BigInt(safe(pct, 'pct')), PCT_HUNDRED)),
    a.currency,
  );
}

/**
 * Splits an amount across non-negative integer weights so the parts sum exactly to the total
 * (largest remainder; ties go to the earlier index). Used for landed-cost allocation (Σ = charge).
 */
export function allocateMoney(total: Money, weights: readonly number[]): Money[] {
  if (weights.length === 0) throw new RangeError('no weights');
  const w = weights.map((x) => {
    if (!Number.isSafeInteger(x) || x < 0)
      throw new RangeError('weights must be non-negative integers');
    return BigInt(x);
  });
  const sumW = w.reduce((a, b) => a + b, 0n);
  if (sumW === 0n) throw new RangeError('weights sum to zero');
  const sign = total.amountMinor < 0 ? -1n : 1n;
  const t = BigInt(Math.abs(total.amountMinor));
  const base = w.map((x) => (t * x) / sumW);
  let left = t - base.reduce((a, b) => a + b, 0n);
  const order = w
    .map((x, i) => ({ i, rem: (t * x) % sumW }))
    .sort((a, b) => (a.rem === b.rem ? a.i - b.i : a.rem > b.rem ? -1 : 1));
  const parts = [...base];
  for (const { i } of order) {
    if (left === 0n) break;
    parts[i] = (parts[i] ?? 0n) + 1n;
    left -= 1n;
  }
  return parts.map((p) => money(toSafeNumber(p * sign), total.currency));
}

// ---- unit rates (micros) ----

export function unitRate(micros: number, currency: CurrencyCode, per: Uom): UnitRate {
  minorUnitsOf(currency);
  return Object.freeze({ micros: safe(micros, 'micros'), currency, per });
}

/** "1.25" CAD per EA → 1 250 000 micros (strict 6 dp). */
export function unitRateFromDecimal(text: string, currency: CurrencyCode, per: Uom): UnitRate {
  return unitRate(toSafeNumber(parseDecimal(text, RATE_SCALE)), currency, per);
}

export function unitRateToDecimal(r: UnitRate): string {
  return formatDecimal(BigInt(r.micros), RATE_SCALE);
}

export const unitRateSchema = z.object({
  micros: z.number().int().refine(Number.isSafeInteger),
  currency: currencyCodeSchema,
  per: uomSchema,
});

/** Extended amount = round-half-even(qty × rate) in minor units of the rate's currency. */
export function extend(q: Qty, rate: UnitRate): Money {
  if (q.uom !== rate.per) {
    throw new AppError('UOM_MISMATCH', {
      messageKey: 'errors.units.uomMismatch',
      params: { left: q.uom, right: rate.per },
    });
  }
  const minor = pow10(minorUnitsOf(rate.currency));
  const num = BigInt(q.milli) * BigInt(rate.micros) * minor;
  return money(toSafeNumber(roundHalfEven(num, QTY_ONE * MICROS)), rate.currency);
}

/** Unit cost of an amount over a quantity (e.g. actual cost ÷ pieces), half-even to micros. */
export function unitRateOf(total: Money, q: Qty): UnitRate {
  if (q.milli === 0) throw new AppError('BUSINESS_RULE', { messageKey: 'errors.units.zeroQty' });
  const minor = pow10(minorUnitsOf(total.currency));
  const num = BigInt(total.amountMinor) * MICROS * QTY_ONE;
  return unitRate(toSafeNumber(roundHalfEven(num, BigInt(q.milli) * minor)), total.currency, q.uom);
}

export function percentOfRate(r: UnitRate, pct: Pct): UnitRate {
  return unitRate(
    toSafeNumber(roundHalfEven(BigInt(r.micros) * BigInt(safe(pct, 'pct')), PCT_HUNDRED)),
    r.currency,
    r.per,
  );
}

export function addRate(a: UnitRate, b: UnitRate): UnitRate {
  if (a.currency !== b.currency) throw currencyMismatch(a.currency, b.currency);
  if (a.per !== b.per) {
    throw new AppError('UOM_MISMATCH', {
      messageKey: 'errors.units.uomMismatch',
      params: { left: a.per, right: b.per },
    });
  }
  return unitRate(a.micros + b.micros, a.currency, a.per);
}

/** Rounds a unit rate to the currency's minor unit (a document line price), half-even. */
export function rateToMoney(r: UnitRate): Money {
  const minor = pow10(minorUnitsOf(r.currency));
  return money(toSafeNumber(roundHalfEven(BigInt(r.micros) * minor, MICROS)), r.currency);
}

// ---- percentages ----

/** "6" → 60 000; "4.25" → 42 500 (numeric(7,4)). */
export function pctFromDecimal(text: string): Pct {
  const v = toSafeNumber(parseDecimal(text, PCT_SCALE));
  if (Math.abs(v) > 999_9999) throw new RangeError('pct outside numeric(7,4)');
  return v;
}

export function pctFromDecimalRounded(text: string): Pct {
  return pctFromDecimal(formatDecimal(parseDecimalRounded(text, PCT_SCALE), PCT_SCALE));
}

export function pctToDecimal(p: Pct): string {
  return formatDecimal(BigInt(safe(p, 'pct')), PCT_SCALE);
}

export const pctSchema = z.number().int().min(-999_9999).max(999_9999);
